import React, { Component } from "react";
import {
  Col,
  Row,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  CustomInput,
} from "reactstrap";
import {
  getVehicleTypesAdmin,
  createVehicleType,
  updateVehicleType,
  deleteVehicleType,
  getTransportSettings,
} from "../../Endpoint/Admin/index";
import BranchAssignment from "../../components/Common/BranchAssignment";
import { toast } from "react-toastify";
import { canWrite } from "../../services/PermissionService";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, MiniStat, StatRow, Table, Chip, Btn, IconBtn, MODAL_CSS,
} from "../../components/Console/ConsoleUI";

/**
 * Vehicle types — the catalogue behind every transport quote, redrawn to the
 * console's fleet screen (D5).
 *
 * A reskin: the same admin list, create, update and delete calls, the same
 * BranchAssignment with its per-branch price overrides, and the same canWrite()
 * gate on every write control.
 *
 * One pre-existing gap is left as it was: the editor has never carried inputs
 * for perKmRate, dayPrice, nightPrice or currency even though the record and
 * the branch overrides use them. Adding fields would change what this screen
 * can save, which is outside a reskin — it is flagged rather than fixed.
 */

const CSS = MODAL_CSS;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/** The services a vehicle type can be offered under, and why each matters. */
const AVAILABLE_FOR = [
  { key: "taxi", label: "Taxi", note: "Short trips within the city. Uses Fixed Amount pricing." },
  { key: "airportTransfer", label: "Airport transfer", note: "To and from the airport. Uses Day/Night pricing." },
  { key: "intercity", label: "Inter-city", note: "Long distance, same-day return. Uses Per KM Rate." },
  { key: "outstation", label: "Outstation", note: "Multi-day trips out of town. Uses Per KM Rate." },
  { key: "dayRental", label: "Day rental", note: "Vehicle hired for a full day. Uses Fixed Amount." },
];

class VehicleTypes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      vehicleTypes: [],
      isLoaded: false,
      showModal: false,
      showDeleteModal: false,
      editingVehicle: null,
      deletingVehicle: null,
      nightRateMultiplier: 1.5, // Default, will be loaded from settings
      formData: {
        code: "",
        name: "",
        description: "",
        maxPassengers: 4,
        maxLuggage: 3,
        fixedAmount: 50,
        perKmRate: 2.0,
        dayPrice: 0,
        nightPrice: 0,
        currency: "USD",
        icon: "ri-car-line",
        displayOrder: 1,
        isActive: true,
        discount: {
          isEnabled: false,
          type: "percentage",
          value: 0,
          displayLabel: "",
        },
        availableFor: {
          taxi: false,
          airportTransfer: false,
          intercity: false,
          outstation: false,
          dayRental: false,
        },
        showPrice: true, // If false, shows "Quote on Request" in mobile app
        branchIds: [],
        excludedBranchIds: [],
        branchPriceOverrides: [],
      },
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Transport", link: "/transport/requests" },
        { title: "Vehicle Types", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadVehicleTypes();
    this.loadSettings();
  }

  loadSettings = () => {
    getTransportSettings()
      .then((result) => {
        if (result && result.status === 200 && result.data) {
          this.setState({
            nightRateMultiplier: result.data.defaultNightRateMultiplier || 1.5,
          });
        }
      })
      .catch(() => console.log("Could not load transport settings"));
  };

  loadVehicleTypes = () => {
    getVehicleTypesAdmin()
      .then((result) => {
        if (result && result.status === 200) {
          this.setState({ vehicleTypes: result.data, isLoaded: true });
        } else {
          toast.error(result?.message || "Failed to load vehicle types");
        }
      })
      .catch(() => toast.error("Error loading vehicle types"));
  };

  getDefaultFormData = () => ({
    code: "",
    name: "",
    description: "",
    maxPassengers: 4,
    maxLuggage: 3,
    fixedAmount: 50,
    perKmRate: 2.0,
    dayPrice: 0,
    nightPrice: 0,
    currency: "USD",
    icon: "ri-car-line",
    displayOrder: this.state.vehicleTypes.length + 1,
    isActive: true,
    discount: {
      isEnabled: false,
      type: "percentage",
      value: 0,
      displayLabel: "",
    },
    availableFor: {
      taxi: false,
      airportTransfer: false,
      intercity: false,
      outstation: false,
      dayRental: false,
    },
    showPrice: true,
    branchIds: [],
    excludedBranchIds: [],
    branchPriceOverrides: [],
  });

  handleAdd = () => {
    this.setState({
      editingVehicle: null,
      formData: this.getDefaultFormData(),
      showModal: true,
    });
  };

  handleEdit = (vehicle) => {
    this.setState({
      editingVehicle: vehicle,
      formData: {
        code: vehicle.code,
        name: vehicle.name,
        description: vehicle.description || "",
        maxPassengers: vehicle.maxPassengers,
        maxLuggage: vehicle.maxLuggage,
        fixedAmount: vehicle.fixedAmount,
        perKmRate: vehicle.perKmRate,
        dayPrice: vehicle.dayPrice || 0,
        nightPrice: vehicle.nightPrice || 0,
        currency: vehicle.currency,
        icon: vehicle.icon || "ri-car-line",
        displayOrder: vehicle.displayOrder,
        isActive: vehicle.isActive,
        discount: vehicle.discount || {
          isEnabled: false,
          type: "percentage",
          value: 0,
          displayLabel: "",
        },
        availableFor: {
          taxi: vehicle.availableFor?.taxi || false,
          airportTransfer: vehicle.availableFor?.airportTransfer || false,
          intercity: vehicle.availableFor?.intercity || false,
          outstation: vehicle.availableFor?.outstation || false,
          dayRental: vehicle.availableFor?.dayRental || false,
        },
        showPrice: vehicle.showPrice !== false, // Default to true if not set
        branchIds: (vehicle.branchIds || []).map((b) =>
          b && b._id ? b._id : String(b)
        ),
        excludedBranchIds: (vehicle.excludedBranchIds || []).map((b) =>
          b && b._id ? b._id : String(b)
        ),
        branchPriceOverrides: vehicle.branchPriceOverrides || [],
      },
      showModal: true,
    });
  };

  handleDelete = (vehicle) => {
    this.setState({
      deletingVehicle: vehicle,
      showDeleteModal: true,
    });
  };

  confirmDelete = () => {
    const { deletingVehicle } = this.state;
    deleteVehicleType(deletingVehicle._id)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Vehicle type deleted successfully");
          this.setState({ showDeleteModal: false, deletingVehicle: null });
          this.loadVehicleTypes();
        } else {
          toast.error(response?.message || "Failed to delete vehicle type");
        }
      })
      .catch(() => toast.error("Error deleting vehicle type"));
  };

  handleSave = () => {
    const { editingVehicle, formData } = this.state;

    if (!formData.code || !formData.name) {
      toast.error("Code and Name are required");
      return;
    }

    const saveData = { ...formData };

    if (editingVehicle) {
      updateVehicleType(editingVehicle._id, saveData)
        .then((response) => {
          if (response && response.status === 200) {
            toast.success("Vehicle type updated successfully");
            this.setState({ showModal: false, editingVehicle: null });
            this.loadVehicleTypes();
          } else {
            toast.error(response?.message || "Failed to update vehicle type");
          }
        })
        .catch(() => toast.error("Error updating vehicle type"));
    } else {
      createVehicleType(saveData)
        .then((response) => {
          if (response && response.status === 200) {
            toast.success("Vehicle type created successfully");
            this.setState({ showModal: false });
            this.loadVehicleTypes();
          } else {
            toast.error(response?.message || "Failed to create vehicle type");
          }
        })
        .catch(() => toast.error("Error creating vehicle type"));
    }
  };

  updateFormData = (field, value) => {
    this.setState((prevState) => {
      const newFormData = {
        ...prevState.formData,
        [field]: value,
      };

      return { formData: newFormData };
    });
  };

  updateDiscount = (field, value) => {
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        discount: {
          ...prevState.formData.discount,
          [field]: value,
        },
      },
    }));
  };

  updateAvailableFor = (field, value) => {
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        availableFor: {
          ...prevState.formData.availableFor,
          [field]: value,
        },
      },
    }));
  };

  /**
   * Price, discounted where a discount is switched on. The struck-through
   * original stays beside it: a rate that quietly changed is a rate nobody
   * can check.
   */
  renderPriceWithDiscount = (vehicle) => {
    const on = vehicle.discount?.isEnabled && vehicle.discount?.value > 0;
    if (!on) {
      return (
        <div>
          <div style={{ fontSize: 15, fontWeight: 500, color: C.ink }}>
            {money(vehicle.fixedAmount)}
          </div>
          <div style={{ marginTop: 2, fontSize: 12, color: C.taupeLight }}>
            {money(vehicle.perKmRate)}/km
          </div>
        </div>
      );
    }

    const pct = vehicle.discount.type === "percentage";
    const discountedFixed = pct
      ? vehicle.fixedAmount * (1 - vehicle.discount.value / 100)
      : vehicle.fixedAmount - vehicle.discount.value;
    const discountedPerKm = pct
      ? vehicle.perKmRate * (1 - vehicle.discount.value / 100)
      : vehicle.perKmRate - vehicle.discount.value / 10;

    return (
      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 7 }}>
          <span style={{ fontSize: 13, color: C.taupeLight, textDecoration: "line-through" }}>
            {money(vehicle.fixedAmount)}
          </span>
          <span style={{ fontSize: 15, fontWeight: 500, color: "#2f7d33" }}>
            {money(discountedFixed)}
          </span>
        </div>
        <div style={{ marginTop: 2, display: "flex", alignItems: "baseline", gap: 7 }}>
          <span style={{ fontSize: 12, color: C.taupeLight, textDecoration: "line-through" }}>
            {money(vehicle.perKmRate)}/km
          </span>
          <span style={{ fontSize: 12, color: "#2f7d33" }}>{money(discountedPerKm)}/km</span>
        </div>
        <div style={{ marginTop: 5 }}>
          <Chip tone="good">
            {vehicle.discount.displayLabel ||
              `${vehicle.discount.value}${pct ? "%" : "$"} OFF`}
          </Chip>
        </div>
      </div>
    );
  };

  columns = () => [
    {
      key: "vehicle",
      label: "Vehicle",
      render: (v) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 40, height: 40, borderRadius: 12, flex: "0 0 40px",
              background: C.iconTint, color: C.brown,
              display: "grid", placeItems: "center",
            }}
          >
            <i className={`${v.icon || "ri-car-line"} font-size-20`} />
          </span>
          <div>
            <div style={{ fontSize: 15, color: C.ink }}>{v.name}</div>
            <div style={{ marginTop: 2, fontSize: 12, letterSpacing: ".6px", color: C.taupeLight }}>
              {v.code}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "capacity",
      label: "Capacity",
      width: 150,
      render: (v) => (
        <div style={{ fontSize: 14, color: C.slate }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="ms" style={{ fontSize: 17, color: C.taupeLight }}>person</span>
            {v.maxPassengers} guests
          </div>
          <div style={{ marginTop: 3, display: "flex", alignItems: "center", gap: 6 }}>
            <span className="ms" style={{ fontSize: 17, color: C.taupeLight }}>luggage</span>
            {v.maxLuggage} bags
          </div>
        </div>
      ),
    },
    {
      key: "pricing",
      label: "Pricing",
      width: 170,
      render: (v) =>
        v.showPrice === false ? (
          <span style={{ color: C.taupeLight }}>Quote on request</span>
        ) : (
          this.renderPriceWithDiscount(v)
        ),
    },
    {
      key: "availableFor",
      label: "Offered for",
      render: (v) => {
        const on = AVAILABLE_FOR.filter((a) => v.availableFor?.[a.key]);
        if (on.length === 0) return <Chip tone="bad">Not offered anywhere</Chip>;
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {on.map((a) => (
              <Chip key={a.key}>{a.label}</Chip>
            ))}
          </div>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      width: 110,
      render: (v) => (
        <Chip tone={v.isActive ? "good" : "neutral"}>{v.isActive ? "Active" : "Off"}</Chip>
      ),
    },
    // Write controls appear as a column only for accounts that may write,
    // matching the canWrite() gate the previous screen put on its Actions cell.
    ...(canWrite()
      ? [
          {
            key: "actions",
            label: "",
            align: "right",
            width: 100,
            render: (v) => (
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                <IconBtn icon="edit" title="Edit vehicle type" onClick={() => this.handleEdit(v)} />
                <IconBtn
                  icon="delete"
                  title="Delete vehicle type"
                  tone="bad"
                  onClick={() => this.handleDelete(v)}
                />
              </div>
            ),
          },
        ]
      : []),
  ];

  render() {
    const { vehicleTypes, showModal, showDeleteModal, editingVehicle, deletingVehicle, formData, isLoaded } = this.state;

    const iconOptions = [
      { value: "ri-car-line", label: "Car" },
      { value: "ri-car-fill", label: "Car (Filled)" },
      { value: "ri-taxi-line", label: "Taxi" },
      { value: "ri-taxi-fill", label: "Taxi (Filled)" },
      { value: "ri-bus-line", label: "Bus/Van" },
      { value: "ri-bus-fill", label: "Bus (Filled)" },
      { value: "ri-truck-line", label: "Truck/SUV" },
      { value: "ri-roadster-line", label: "Roadster/Sports" },
      { value: "ri-vip-crown-line", label: "VIP/Luxury" },
      { value: "ri-vip-crown-fill", label: "VIP (Filled)" },
      { value: "ri-e-bike-line", label: "Bike/Motorcycle" },
      { value: "ri-motorbike-line", label: "Motorbike" },
      { value: "ri-riding-line", label: "Auto Rickshaw" },
      { value: "ri-caravan-line", label: "Van/Caravan" },
      { value: "ri-train-line", label: "Train/Metro" },
      { value: "ri-flight-takeoff-line", label: "Airport" },
      { value: "ri-ship-line", label: "Boat/Ship" },
    ];

    const rows = [...vehicleTypes].sort(
      (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
    );
    const active = vehicleTypes.filter((v) => v.isActive).length;
    const seats = vehicleTypes.reduce((s, v) => s + Number(v.maxPassengers || 0), 0);
    const discounted = vehicleTypes.filter(
      (v) => v.discount?.isEnabled && v.discount?.value > 0
    ).length;
    const unoffered = vehicleTypes.filter(
      (v) => !AVAILABLE_FOR.some((a) => v.availableFor?.[a.key])
    ).length;

    return (
      <ConsolePage
        title="Vehicle types"
        subtitle={`Transport · ${vehicleTypes.length} type${vehicleTypes.length === 1 ? "" : "s"}${isLoaded ? "" : " · loading…"}`}
        actions={
          <React.Fragment>
            <Btn
              kind="quiet"
              icon="workspace_premium"
              onClick={() => this.props.history.push("/transport/service-tiers")}
            >
              Service tiers
            </Btn>
            {canWrite() && (
              <Btn icon="add" onClick={this.handleAdd}>
                Add vehicle type
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style>{CSS}</style>

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <StatRow>
            <MiniStat
              label="Types bookable"
              value={`${active} / ${vehicleTypes.length}`}
              hint={`${vehicleTypes.length - active} switched off`}
            />
            <MiniStat label="Seats across the fleet" value={seats} hint="sum of stated capacity" />
            <MiniStat
              label="Running a discount"
              value={discounted}
              hint="price shown cut in the app"
            />
            <MiniStat
              label="Offered for nothing"
              value={unoffered}
              hint="no service selected"
              tone={unoffered ? "bad" : undefined}
            />
          </StatRow>

          <Panel style={{ padding: "6px 10px" }}>
            {!isLoaded ? (
              <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
                Loading vehicle types…
              </div>
            ) : (
              <Table
                columns={this.columns()}
                rows={rows}
                empty="No vehicle types yet — run database/seedVehicleTypes.js or add one."
              />
            )}
          </Panel>
        </div>

        {/* Add/Edit Modal */}
        <Modal
          isOpen={showModal}
          toggle={() => this.setState({ showModal: false })}
          size="lg"
          className="con-modal"
        >
          <ModalHeader toggle={() => this.setState({ showModal: false })}>
            {editingVehicle ? `Edit: ${editingVehicle.name}` : "Add vehicle type"}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Code *</Label>
                  <Input
                    type="text"
                    value={formData.code}
                    onChange={(e) => this.updateFormData("code", e.target.value.toUpperCase())}
                    placeholder="e.g., SEDAN, SUV"
                    disabled={!!editingVehicle}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Name *</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => this.updateFormData("name", e.target.value)}
                    placeholder="e.g., Sedan, SUV"
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label>Description</Label>
                  <Input
                    type="textarea"
                    rows={2}
                    value={formData.description}
                    onChange={(e) => this.updateFormData("description", e.target.value)}
                    placeholder="Brief description of this vehicle type"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Max Passengers</Label>
                  <Input
                    type="number"
                    value={formData.maxPassengers}
                    onChange={(e) => this.updateFormData("maxPassengers", parseInt(e.target.value) || 0)}
                    min="1"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Max Luggage</Label>
                  <Input
                    type="number"
                    value={formData.maxLuggage}
                    onChange={(e) => this.updateFormData("maxLuggage", parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Icon</Label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span
                      style={{
                        width: 46, height: 46, borderRadius: 12, flex: "0 0 46px",
                        background: C.iconTint, color: C.brown,
                        display: "grid", placeItems: "center",
                      }}
                    >
                      <i className={`${formData.icon}`} style={{ fontSize: 20 }} />
                    </span>
                    <Input
                      type="select"
                      value={formData.icon}
                      onChange={(e) => this.updateFormData("icon", e.target.value)}
                      style={{ flex: 1 }}
                    >
                      {iconOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Input>
                  </div>
                  <small style={{ color: C.taupeLight }}>Icon displayed in mobile app</small>
                </FormGroup>
              </Col>

              <Col xs={12}>
                <SectionTitle
                  right={
                    <FormGroup check style={{ margin: 0 }}>
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={formData.showPrice}
                          onChange={(e) => this.updateFormData("showPrice", e.target.checked)}
                        />{" "}
                        Show price in app
                      </Label>
                    </FormGroup>
                  }
                >
                  Pricing
                </SectionTitle>
                {!formData.showPrice && (
                  <div
                    style={{
                      marginBottom: 16, padding: "12px 15px", borderRadius: 12,
                      background: C.iconTint, fontSize: 13, color: C.slate,
                    }}
                  >
                    The mobile app will show <strong>“Quote on Request”</strong> instead of a price.
                  </div>
                )}
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Fixed Amount (Taxi)</Label>
                  <Input
                    type="number"
                    value={formData.fixedAmount}
                    onChange={(e) => this.updateFormData("fixedAmount", parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                    disabled={!formData.showPrice}
                  />
                  <small style={{ color: C.taupeLight }}>Used for local taxi rides</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => this.updateFormData("displayOrder", parseInt(e.target.value) || 1)}
                    min="1"
                  />
                  <small style={{ color: C.taupeLight }}>Lower numbers appear first</small>
                </FormGroup>
              </Col>

              <Col xs={12}>
                <SectionTitle>Discount</SectionTitle>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Enable Discount</Label>
                  <div>
                    <CustomInput
                      type="switch"
                      id="discountEnabled"
                      checked={formData.discount.isEnabled}
                      onChange={(e) => this.updateDiscount("isEnabled", e.target.checked)}
                    />
                  </div>
                </FormGroup>
              </Col>
              {formData.discount.isEnabled && (
                <React.Fragment>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Discount Type</Label>
                      <Input
                        type="select"
                        value={formData.discount.type}
                        onChange={(e) => this.updateDiscount("type", e.target.value)}
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount ($)</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Discount Value</Label>
                      <Input
                        type="number"
                        value={formData.discount.value}
                        onChange={(e) => this.updateDiscount("value", parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Display Label</Label>
                      <Input
                        type="text"
                        value={formData.discount.displayLabel}
                        onChange={(e) => this.updateDiscount("displayLabel", e.target.value)}
                        placeholder="e.g., 10% OFF"
                      />
                    </FormGroup>
                  </Col>
                </React.Fragment>
              )}

              <Col xs={12}>
                <SectionTitle>Offered for</SectionTitle>
                <div style={{ marginTop: -8, marginBottom: 14, fontSize: 13, color: C.taupeLight }}>
                  Which services this vehicle type appears under in the mobile app.
                </div>
              </Col>
              {AVAILABLE_FOR.map((a) => {
                const on = !!formData.availableFor[a.key];
                return (
                  <Col md={4} key={a.key} style={{ marginBottom: 14 }}>
                    <div
                      style={{
                        padding: "14px 16px", borderRadius: 14, height: "100%",
                        border: `1.5px solid ${on ? C.brown : C.tint}`,
                        background: on ? C.surface : "#fff",
                      }}
                    >
                      <FormGroup check style={{ marginBottom: 6 }}>
                        <Label check>
                          <Input
                            type="checkbox"
                            checked={on}
                            onChange={(e) => this.updateAvailableFor(a.key, e.target.checked)}
                          />{" "}
                          <strong>{a.label}</strong>
                        </Label>
                      </FormGroup>
                      <div style={{ fontSize: 13, color: C.taupe, lineHeight: 1.45 }}>{a.note}</div>
                    </div>
                  </Col>
                );
              })}

              <Col xs={12}>
                <SectionTitle>Branches</SectionTitle>
                <BranchAssignment
                  branchIds={formData.branchIds}
                  excludedBranchIds={formData.excludedBranchIds}
                  overrides={formData.branchPriceOverrides}
                  priceFields={[
                    { key: "fixedAmount", label: "Fixed Amount" },
                    { key: "perKmRate", label: "Per KM Rate" },
                    { key: "dayPrice", label: "Day Price" },
                    { key: "nightPrice", label: "Night Price" },
                  ]}
                  onChange={({ branchIds, excludedBranchIds, overrides }) =>
                    this.setState((prev) => ({
                      formData: {
                        ...prev.formData,
                        branchIds,
                        excludedBranchIds,
                        branchPriceOverrides: overrides,
                      },
                    }))
                  }
                />
              </Col>

              <Col xs={12} style={{ marginTop: 12 }}>
                <FormGroup check>
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => this.updateFormData("isActive", e.target.checked)}
                    />{" "}
                    Vehicle type is active
                  </Label>
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Btn kind="quiet" onClick={() => this.setState({ showModal: false })}>
              Cancel
            </Btn>
            <Btn onClick={this.handleSave}>
              {editingVehicle ? "Update" : "Create"}
            </Btn>
          </ModalFooter>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          toggle={() => this.setState({ showDeleteModal: false })}
          className="con-modal"
          centered
        >
          <ModalHeader toggle={() => this.setState({ showDeleteModal: false })}>
            Delete vehicle type
          </ModalHeader>
          <ModalBody>
            <p style={{ fontSize: 15, color: C.slate }}>
              Delete <strong>{deletingVehicle?.name}</strong>? Guests will no longer be able to
              book it, and this cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <Btn kind="quiet" onClick={() => this.setState({ showDeleteModal: false })}>
              Cancel
            </Btn>
            <Btn kind="danger" onClick={this.confirmDelete}>
              Delete
            </Btn>
          </ModalFooter>
        </Modal>
      </ConsolePage>
    );
  }
}

/** Section rule inside the editor, with an optional control on its right. */
const SectionTitle = ({ children, right }) => (
  <div
    style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
      margin: "6px 0 16px", paddingTop: 16, borderTop: `1px solid ${C.tint}`,
      fontSize: 12, letterSpacing: ".8px", textTransform: "uppercase", color: C.taupe,
    }}
  >
    <span>{children}</span>
    {right}
  </div>
);

export default VehicleTypes;
